import React, { useState, useMemo, useContext } from 'react';
import { Table, Button, Tag, Tooltip } from 'antd';
import { BarChart3, Bolt } from 'lucide-react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import PnlMtmModal from '../../../common/PnlMtmModal';
import AppContext from '../../../../context/Context';
import { fmtINR } from '../../../../util/helper';
const onSlActionLabels = {
  0: "Default",
  1: "Close + SL Cost (All legs)",
  2: "Close + SL Cost (Profitable legs only)",
  3: "Close Strategy",
  4: "Close + Re-enter",
  5: "Close + Open another strategy"
};

const PositionsTable = ({ positions, loading, onOpenLegModal, strategies = [] }) => {
  const {
    config: { isDark },
  } = useContext(AppContext);
  const [isPnlModalOpen, setIsPnlModalOpen] = useState(false);
  const [pnlModalConfig, setPnlModalConfig] = useState({ title: 'PNL / MTM - Position', defaultLevel: 'position', brokerId: null, brokerName: null, baskets: [], positions: [] });
  const summaryPositions = useMemo(() => (positions || []), [positions]);
  const totalGreeks = useMemo(() => {
    const sum = (key) =>
      (summaryPositions || []).reduce((acc, p) => acc + (Number(p?.[key]) || 0), 0);
    return {
      iv: sum('iv'),
      delta: sum('delta'),
      theta: sum('theta'),
      vega: sum('vega'),
      gamma: sum('gamma')
    };
  }, [summaryPositions]);

  const brokersList = useSelector((state) => state?.brokers?.list || []);
  const getBrokerLabel = (id) => {
    const b = (brokersList || []).find(x => (x?.id?.toString?.() || '') === id?.toString?.());
    if (!b) return id;
    return b?.name?.toUpperCase() || `${b?.type?.toUpperCase?.()} - ${b?.userName?.toUpperCase?.()}` || id;
  };

  const positionColumns = [
    {
      title: 'Display Name',
      align: 'left',
      dataIndex: ['instrument', 'displayName'],
      key: 'displayName',
      width: '14%',
      ellipsis: true,
      onCell: (record) => ({
        style: Number(record?.netQuantity || 0) === 0 ? { opacity: 0.5, pointerEvents: 'none' } : {}
      })
    },
    {
      title: 'Symbol',
      align: 'left',
      dataIndex: ['instrument', 'tradingSymbol'],
      key: 'symbol',
      width: '12%',
      ellipsis: true,
      onCell: (record) => ({
        style: Number(record?.netQuantity || 0) === 0 ? { opacity: 0.5, pointerEvents: 'none' } : {}
      })
    },
    {
      title: 'Product',
      align: 'center',
      dataIndex: 'product',
      key: 'product',
      width: '4%',
      render: (product) => (
        <p style={{ fontSize: 12 }} className='product-text'>{product}</p>
      ),
      onCell: (record) => ({
        style: Number(record?.netQuantity || 0) === 0 ? { opacity: 0.5, pointerEvents: 'none' } : {}
      })
    },
    {
      title: 'Broker',
      align: 'center',
      dataIndex: 'brokerId',
      key: 'brokerAccount',
      width: '8%',
      render: (brokerId) => (
        <Tag color="purple">
          {getBrokerLabel(brokerId)}
        </Tag>
      ),
      onCell: (record) => ({
        style: Number(record?.netQuantity || 0) === 0 ? { opacity: 0.5, pointerEvents: 'none' } : {}
      })
    },
    {
      title: 'Avg Price',
      dataIndex: 'averagePrice',
      key: 'avgPrice',
      width: '7%',
      align: 'right',
      render: (price) => price ? Number(price).toFixed(2) : '-',
      onCell: (record) => ({
        style: Number(record?.netQuantity || 0) === 0 ? { opacity: 0.5, pointerEvents: 'none' } : {}
      })
    },
    {
      title: 'Lots(Qty)',
      dataIndex: 'netQuantity',
      key: 'quantity',
      width: '6%',
      align: 'right',
      render: (qty) => (
        <span style={{ color: qty > 0 ? (isDark ? '#00d27a' : '#16a34a') : qty < 0 ? (isDark ? '#F87171' : '#E74C3C') : '#6b7280' }}>
          {qty}
        </span>
      ),
      onCell: (record) => ({
        style: Number(record?.netQuantity || 0) === 0 ? { opacity: 0.5, pointerEvents: 'none' } : {}
      })
    },
    {
      title: 'LTP',
      dataIndex: 'ltp',
      key: 'ltp',
      width: '6%',
      align: 'right',
      render: (ltp) => ltp ? Number(ltp).toFixed(2) : '-',
      onCell: (record) => ({
        style: Number(record?.netQuantity || 0) === 0 ? { opacity: 0.5, pointerEvents: 'none' } : {}
      })
    },
    {
      title: 'PnL',
      dataIndex: 'totalPNL',
      key: 'tpnl',
      align: 'right',
      width: '8%',
      render: (pnl, record) => (
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 6 }}>
          <span style={{ color: Number(pnl) === 0 ? '' : (Number(pnl) > 0 ? (isDark ? '#00d27a' : '#16a34a') : (isDark ? '#F87171' : '#E74C3C')), whiteSpace: "nowrap" }}>
            {Number(pnl) > 0 ? `+${fmtINR(Number(pnl).toFixed(2))}` : (pnl ? fmtINR(Number(pnl).toFixed(2)) : '0.00')}
          </span>
          <Button
            type="text"
            size="small"
            icon={<BarChart3 strokeWidth={1.5} size={16} className="pnl-graph" />}
            onClick={() => {
              setPnlModalConfig({
                title: `PNL / MTM - ${record?.instrument?.tradingSymbol || record?.instrument?.displayName || 'Position'}`,
                defaultLevel: 'position',
                brokerId: null,
                brokerName: null,
                baskets: [],
                positions: [{ id: record.id, name: record?.instrument?.tradingSymbol || record?.instrument?.displayName || record.id }],
              });
              setIsPnlModalOpen(true);
            }}
          />
        </div>
      ),
    },
    {
      title: 'Unrealized',
      dataIndex: 'unrealizedPNL',
      key: 'upnl',
      width: '8%',
      align: 'right',
      render: (pnl) => (
        <span style={{ color: Number(pnl) === 0 ? '' : (Number(pnl) > 0 ? (isDark ? '#00d27a' : '#16a34a') : (isDark ? '#F87171' : '#E74C3C')), whiteSpace: "nowrap" }}>
          {pnl ? Number(pnl) > 0 ? `+${fmtINR(Number(pnl || 0).toFixed(2))}` : fmtINR(Number(pnl || 0).toFixed(2)) : '0.00'}
        </span>
      ),
    },
    {
      title: 'Realized',
      dataIndex: 'realizedPNL',
      key: 'rpnl',
      width: '8%',
      align: 'right',
      render: (pnl) => (
        <span style={{ color: Number(pnl) >= 0 ? (isDark ? '#00d27a' : '#16a34a') : (isDark ? '#F87171' : '#E74C3C'), whiteSpace: "nowrap" }}>
          {pnl ? Number(pnl) > 0 ? `+${fmtINR(Number(pnl || 0).toFixed(2))}` : fmtINR(Number(pnl || 0).toFixed(2)) : '0.00'}
        </span>
      ),
    },
    {
      title: 'Expiry',
      dataIndex: ['instrument', 'expiry'],
      key: 'expiry',
      width: '20%',
      align: 'center',
      render: (expiry) => {
        if (!expiry) return '-';
        const d = moment(expiry);
        if (!d.isValid()) return '-';
        return (
          <span>{d.format('DD-MM-YYYY')}</span>
        );
      },
      onCell: (record) => ({
        style: Number(record?.netQuantity || 0) === 0 ? { opacity: 0.5, pointerEvents: 'none' } : {}
      })
    },
    {
      title: 'IV',
      dataIndex: 'iv',
      key: 'iv',
      width: '5%',
      align: 'right',
      render: (iv) => (
        <span style={{ color: Number(iv) >= 0 ? (isDark ? '#00d27a' : '#16a34a') : (isDark ? '#F87171' : '#E74C3C') }}>
          {iv ? Number(iv).toFixed(2) : '0.00'}
        </span>
      ),
      onCell: (record) => ({
        style: Number(record?.netQuantity || 0) === 0 ? { opacity: 0.5, pointerEvents: 'none' } : {}
      })
    },
    {
      title: 'Delta',
      dataIndex: 'delta',
      key: 'delta',
      width: '5%',
      align: 'right',
      render: (delta) => (
        <span style={{ color: Number(delta) >= 0 ? (isDark ? '#00d27a' : '#16a34a') : (isDark ? '#F87171' : '#E74C3C') }}>
          {delta ? Number(delta).toFixed(2) : '0.00'}
        </span>
      ),
      onCell: (record) => ({
        style: Number(record?.netQuantity || 0) === 0 ? { opacity: 0.5, pointerEvents: 'none' } : {}
      })
    },
    {
      title: 'Theta',
      dataIndex: 'theta',
      key: 'theta',
      width: '5%',
      align: 'right',
      render: (theta) => (
        <span style={{ color: Number(theta) >= 0 ? (isDark ? '#00d27a' : '#16a34a') : (isDark ? '#F87171' : '#E74C3C') }}>
          {theta ? Number(theta).toFixed(2) : '0.00'}
        </span>
      ),
      onCell: (record) => ({
        style: Number(record?.netQuantity || 0) === 0 ? { opacity: 0.5, pointerEvents: 'none' } : {}
      })
    },
    {
      title: 'Vega',
      dataIndex: 'vega',
      key: 'vega',
      width: '5%',
      align: 'right',
      render: (vega) => (
        <span style={{ color: Number(vega) >= 0 ? (isDark ? '#00d27a' : '#16a34a') : (isDark ? '#F87171' : '#E74C3C') }}>
          {vega ? Number(vega).toFixed(2) : '0.00'}
        </span>
      ),
      onCell: (record) => ({
        style: Number(record?.netQuantity || 0) === 0 ? { opacity: 0.5, pointerEvents: 'none' } : {}
      })
    },
    {
      title: 'Gamma',
      dataIndex: 'gamma',
      key: 'gamma',
      width: '5%',
      align: 'right',
      render: (gamma) => (
        <span style={{ color: Number(gamma) >= 0 ? (isDark ? '#00d27a' : '#16a34a') : (isDark ? '#F87171' : '#E74C3C') }}>
          {gamma ? Number(gamma).toFixed(4) : '0.00'}
        </span>
      ),
      onCell: (record) => ({
        style: Number(record?.netQuantity || 0) === 0 ? { opacity: 0.5, pointerEvents: 'none' } : {}
      })
    },
    {
      title: 'Armed Action',
      key: 'onSlAction',
      width: '10%',
      align: 'center',
      render: (_, record) => {
        const action = record.onSlAction || 0;
        const label = onSlActionLabels[action] || "Default";
        let tooltip = label;
        if (action === 4 && record.onSlReEntryCount) {
          tooltip += ` (Max: ${record.onSlReEntryCount})`;
        } else if (action === 5 && record.onSlTargetStrategyId) {
          const targetStrategy = (strategies || []).find(s => String(s.id) === String(record.onSlTargetStrategyId));
          tooltip += ` (${targetStrategy?.name || record.onSlTargetStrategyId})`;
        }
        return (
          <Tooltip title={tooltip}>
            <span style={{ fontSize: '12px' }}>{label}</span>
          </Tooltip>
        );
      },
      onCell: (record) => ({
        style: Number(record?.netQuantity || 0) === 0 ? { opacity: 0.5, pointerEvents: 'none' } : {}
      })
    },
    {
      title: 'Risk Settings',
      key: 'riskSettings',
      width: '6%',
      align: 'center',
      render: (_, record) => (
        <Tooltip title="Edit Risk Settings">
          <Button
            type="text"
            icon={<Bolt strokeWidth={1.5} size={16} />}
            size="small"
            onClick={() => onOpenLegModal(record)}
            disabled={record.netQuantity === 0}
            style={{
              color: 'inherit'
            }}
          />
        </Tooltip>
      ),
      onCell: (record) => ({
        style: Number(record?.netQuantity || 0) === 0 ? { opacity: 0.5, pointerEvents: 'none' } : {}
      })
    },

  ];

  return (
    <div>
      <Table
        columns={positionColumns}
        dataSource={positions}
        rowKey="id"
        loading={loading}
        pagination={false}
        size="small"
        scroll={{ x: true }}
        locale={{ emptyText: 'No positions found' }}
        style={{ width: '100%' }}
        tableLayout="fixed"
        summary={Array.isArray(summaryPositions) && summaryPositions.length > 0 ? () => (
          <Table.Summary>
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={12}>
                <strong>Total Greeks</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={11} align="right">
                <span style={{ color: Number(totalGreeks.delta) === 0 ? '' : (Number(totalGreeks.delta) > 0 ? (isDark ? '#00d27a' : '#16a34a') : (isDark ? '#F87171' : '#E74C3C')), fontSize: 12 }}>
                  {Number(totalGreeks.delta) > 0 ? '+' : ''}{Number(totalGreeks.delta || 0).toFixed(2)}
                </span>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={12} align="right">
                <span style={{ color: Number(totalGreeks.theta) === 0 ? '' : (Number(totalGreeks.theta) > 0 ? (isDark ? '#00d27a' : '#16a34a') : (isDark ? '#F87171' : '#E74C3C')), fontSize: 12 }}>
                  {Number(totalGreeks.theta) > 0 ? '+' : ''}{Number(totalGreeks.theta || 0).toFixed(2)}
                </span>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={13} align="right">
                <span style={{ color: Number(totalGreeks.vega) === 0 ? '' : (Number(totalGreeks.vega) > 0 ? (isDark ? '#00d27a' : '#16a34a') : (isDark ? '#F87171' : '#E74C3C')), fontSize: 12 }}>
                  {Number(totalGreeks.vega) > 0 ? '+' : ''}{Number(totalGreeks.vega || 0).toFixed(2)}
                </span>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={14} align="right">
                <span style={{ color: Number(totalGreeks.gamma) === 0 ? '' : (Number(totalGreeks.gamma) > 0 ? (isDark ? '#00d27a' : '#16a34a') : (isDark ? '#F87171' : '#E74C3C')), fontSize: 12 }}>
                  {Number(totalGreeks.gamma) > 0 ? '+' : ''}{Number(totalGreeks.gamma || 0).toFixed(2)}
                </span>
              </Table.Summary.Cell>
              {/* Risk Settings column */}
              <Table.Summary.Cell index={15} />
            </Table.Summary.Row>
          </Table.Summary>
        ) : undefined}
      />
      {isPnlModalOpen && (
        <PnlMtmModal
          show={isPnlModalOpen}
          onHide={() => setIsPnlModalOpen(false)}
          title={pnlModalConfig.title}
          defaultLevel={pnlModalConfig.defaultLevel}
          brokerId={pnlModalConfig.brokerId}
          brokerName={pnlModalConfig.brokerName}
          baskets={pnlModalConfig.baskets}
          positions={pnlModalConfig.positions}
          selection={false}
        />
      )}
    </div>
  );
};

export default PositionsTable;
